"use client";

import React, { useState, useEffect } from "react";
import { Compass, Cpu, Globe as Globe2, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-sizes"
};
const sectionContent = {
  eyebrow: {
    vi: "NỀN TẢNG HỌC VIỆN",
    en: "ACADEMY FOUNDATION"
  },
  heading: {
    vi: "Giá trị cốt lõi",
    en: "Our core values"
  },
  subheading: {
    vi: "Bốn nguyên tắc định hình phương pháp chúng tôi giảng dạy Coding, truyền cảm hứng nghiên cứu Engineering và đồng hành cùng học viên qua từng thử thách phòng lab chuẩn mực.",
    en: "Four principles that shape how we teach code, guide experiments, and mentor every student through rigorous lab challenges."
  },
  ctaButton: {
    vi: "Khám phá các chương trình",
    en: "Explore our programs"
  },
  standardBadge: {
    vi: "Tiêu chuẩn giảng dạy",
    en: "Instruction standard"
  }
};
const valuesData = [{
  id: "curiosity",
  code: "VAL_01",
  accentClass: "bg-secondary/15 text-secondary",
  icon: Compass,
  title: {
    vi: "Tinh thần tò mò",
    en: "Curiosity"
  },
  description: {
    vi: "Khuyến khích học viên chủ động tháo mở thiết bị, tìm hiểu sâu bản chất thuật toán và biến từng lỗi kỹ thuật bất ngờ thành cơ hội khám phá.",
    en: "We encourage students to take devices apart, ask why algorithms work, and treat unexpected bugs as invitations to discover."
  }
}, {
  id: "hands-on",
  code: "VAL_02",
  accentClass: "bg-accent/15 text-accent",
  icon: Cpu,
  title: {
    vi: "Học qua thực hành",
    en: "Hands-on learning"
  },
  description: {
    vi: "Mỗi giờ học đều gắn liền với mạch vi điều khiển thực tế, mô hình LEGO chuyên dụng và trực tiếp Coding, nói không với bài giảng lý thuyết thụ động.",
    en: "Every lesson centers on real breadboards, microcontrollers, and written code rather than passive slides or abstract lectures."
  }
}, {
  id: "standards",
  code: "VAL_03",
  accentClass: "bg-secondary/15 text-secondary",
  icon: Globe2,
  title: {
    vi: "Chuẩn mực quốc tế",
    en: "Global standards"
  },
  description: {
    vi: "Giáo trình bám sát chuẩn giáo dục STEM/STEAM thế giới cùng các kỳ thi đấu uy tín, giúp hồ sơ năng lực của học viên tự tin hội nhập mọi nơi.",
    en: "Our curriculum aligns with international STEM frameworks and competition benchmarks so student portfolios carry weight anywhere."
  }
}, {
  id: "character",
  code: "VAL_04",
  accentClass: "bg-primary/10 text-primary",
  icon: ShieldCheck,
  title: {
    vi: "Rèn luyện bản lĩnh",
    en: "Character building"
  },
  description: {
    vi: "Nuôi dưỡng tính kiên trì trong Engineering, sự phối hợp đội nhóm văn minh và phong thái điềm tĩnh ngay cả trong các vòng đấu Robotics áp lực cao.",
    en: "We cultivate technical perseverance, honest peer collaboration, and calm composure during high-pressure competition rounds."
  }
}];
export default function Values() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const readStorageLang = (): "vi" | "en" => {
      try {
        const saved = localStorage.getItem("roboto_lang");
        return saved === "en" ? "en" : "vi";
      } catch {
        return "vi";
      }
    };
    setLang(readStorageLang());
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      } else {
        setLang(readStorageLang());
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        setLang(e.newValue === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-core-values" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-24 border-t border-border overflow-hidden">
      {/* Decorative Floating Mascot */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute top-4 right-4 sm:top-6 sm:right-8 lg:top-8 lg:right-12 xl:right-16 z-0">
        <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain bg-transparent pointer-events-none select-none drop-shadow-sm" loading="lazy" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 xl:px-24">
        {/* Lab Blueprint spec rail */}
        <div className="flex items-center gap-2 mb-6" aria-hidden="true">
          <div className="size-1.5 bg-secondary" />
          <div className="h-px w-24 bg-secondary/60" />
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
            {sectionContent.eyebrow[lang]}
          </span>
        </div>

        {/* Section Heading & Framing */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 lg:mb-16">
          <div className="max-w-2xl pr-16 sm:pr-0">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              {sectionContent.heading[lang]}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              {sectionContent.subheading[lang]}
            </p>
          </div>

          <div className="shrink-0">
            <Button variant="outline" asChild className="border-primary/20 hover:border-primary text-foreground font-sans font-medium">
              <Link to="/ProgramsPage">{sectionContent.ctaButton[lang]}</Link>
            </Button>
          </div>
        </div>

        {/* 4-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {valuesData.map((val, i) => {
          const IconComponent = val.icon;
          return <motion.div key={val.id} data-index={i} variants={cardVariants} className="h-full">
                <Card className="h-full bg-card border border-border rounded-xl p-6 sm:p-7 flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-md">
                  <div>
                    {/* Header info & technical squircle icon */}
                    <CardHeader className="p-0 mb-6 gap-0">
                      <div className="flex items-center justify-between w-full mb-6">
                        <div className={`size-12 rounded-xl flex items-center justify-center transition-colors ${val.accentClass}`}>
                          <IconComponent className="size-6 stroke-[1.75]" />
                        </div>
                        <span className="font-mono text-xs font-semibold text-muted-foreground">
                          {val.code}
                        </span>
                      </div>
                      <CardTitle className="font-serif text-xl font-semibold text-card-foreground">
                        <h3 className="font-serif text-xl font-semibold text-card-foreground">
                          {val.title[lang]}
                        </h3>
                      </CardTitle>
                    </CardHeader>

                    {/* Single defining sentence */}
                    <CardContent className="p-0">
                      <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {val.description[lang]}
                      </p>
                    </CardContent>
                  </div>

                  {/* Blueprint status indicator */}
                  <div className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground uppercase">
                      {sectionContent.standardBadge[lang]}
                    </span>
                    <div className="size-1.5 rounded-full bg-secondary" />
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}